import { useCallback, useEffect, useState } from 'react';
import { BackendApi } from '../../../../../../../api/api';
import {
    BKS_DOCUMENT_IMPORT_MAPPINGS_SETTING_NAME,
    BKSDocumentImportMappings,
    BKSEntrepreneurBusiness,
    BKSImportJobSummary,
    BKSImportParams,
    BKSImportStatusResponse,
    BKSJournal,
    InitiateBKSImportResult,
} from './bksDocumentImport.types';
import { BackendAPITypes } from '../../../../../../../generated/blueprint';

export type BookkeepingConnection = BackendAPITypes.BACKENDPreHandleSyncApiV1BusinessMyConnectionsGetResponseBody[number];

function parseBKSDocumentImportMappings(raw: unknown): BKSDocumentImportMappings {
    return typeof raw === 'object' && raw !== null && !Array.isArray(raw)
        ? raw as BKSDocumentImportMappings
        : {};
}

export interface BKSDocumentImportActions {
    onLoadBKSAdministrations: (connectionId: string) => Promise<BKSEntrepreneurBusiness[]>;
    onLoadBKSJournals: (administrationId: string, connectionId: string) => Promise<BKSJournal[]>;
    onInitiateBKSImport: (params: BKSImportParams) => Promise<InitiateBKSImportResult>;
    onPollBKSImportStatus: (importId: string) => Promise<BKSImportStatusResponse>;
    onRefreshImportJobs: () => Promise<BKSImportJobSummary[]>;
    onSaveConnectionMapping: (connectionId: string, mapping: BKSDocumentImportMappings[string]) => Promise<void>;
    getConnectionMapping: (connectionId: string) => BKSDocumentImportMappings[string] | undefined;
}

export interface BKSDocumentImportState extends BKSDocumentImportActions {
    hasIntegration: boolean | null;
    connections: BookkeepingConnection[];
    importJobs: BKSImportJobSummary[];
    mappings: BKSDocumentImportMappings;
    isLoadingJobs: boolean;
}

export function useBKSDocumentImport(api: BackendApi, businessId: string | undefined): BKSDocumentImportState {
    const [hasIntegration, setHasIntegration] = useState<boolean | null>(null);
    const [connections, setConnections] = useState<BookkeepingConnection[]>([]);
    const [importJobs, setImportJobs] = useState<BKSImportJobSummary[]>([]);
    const [mappings, setMappings] = useState<BKSDocumentImportMappings>({});
    const [isLoadingJobs, setIsLoadingJobs] = useState(false);
    const token = api.token;

    useEffect(() => {
        let cancelled = false;
        const client = new BackendApi(token);
        (async () => {
            try {
                const integrations = await client.Business__myConnections({});
                const connectedIntegrations = integrations.filter((integration) => integration.status === 'CONNECTED');
                if (!cancelled) {
                    setHasIntegration(connectedIntegrations.length > 0);
                    setConnections(connectedIntegrations);
                }
            } catch {
                if (!cancelled) {
                    setHasIntegration(false);
                    setConnections([]);
                }
            }
        })();
        return () => { cancelled = true; };
    }, [token]);

    const loadMappings = useCallback(async () => {
        if (!businessId) {
            return;
        }
        const response = await api.Partner__business__settings(businessId);
        setMappings(parseBKSDocumentImportMappings(response.settings[BKS_DOCUMENT_IMPORT_MAPPINGS_SETTING_NAME]));
    }, [api, businessId]);

    useEffect(() => {
        void loadMappings();
    }, [loadMappings]);

    const onRefreshImportJobs = useCallback(async () => {
        if (!businessId) {
            return [];
        }
        setIsLoadingJobs(true);
        try {
            const jobs = await api.Partner__getBksImportJobs(businessId) as BKSImportJobSummary[];
            setImportJobs(jobs);
            return jobs;
        } finally {
            setIsLoadingJobs(false);
        }
    }, [api, businessId]);

    useEffect(() => {
        if (businessId && hasIntegration) {
            void onRefreshImportJobs();
        }
    }, [businessId, hasIntegration, onRefreshImportJobs]);

    const onLoadBKSAdministrations = useCallback(async (connectionId: string) => {
        return await api.Partner__getBksEntrepreneurBusinesses(connectionId);
    }, [api]);

    const onLoadBKSJournals = useCallback(async (administrationId: string, connectionId: string) => {
        return await api.Partner__getBksJournals(administrationId, connectionId);
    }, [api]);

    const onSaveConnectionMapping = useCallback(async (
        connectionId: string,
        mapping: BKSDocumentImportMappings[string],
    ) => {
        if (!businessId) {
            return;
        }
        const response = await api.Partner__business__settings(businessId);
        const updatedMappings: BKSDocumentImportMappings = {
            ...parseBKSDocumentImportMappings(response.settings[BKS_DOCUMENT_IMPORT_MAPPINGS_SETTING_NAME]),
            [connectionId]: mapping,
        };
        await api.Partner__business__updateSetting({
            business_id: businessId,
            setting_name: BKS_DOCUMENT_IMPORT_MAPPINGS_SETTING_NAME,
            setting_value: updatedMappings,
        });
        setMappings(updatedMappings);
    }, [api, businessId]);

    const getConnectionMapping = useCallback((connectionId: string) => {
        return mappings[connectionId];
    }, [mappings]);

    const onInitiateBKSImport = useCallback(async (params: BKSImportParams) => {
        if (!businessId) {
            throw new Error('Business ID is required');
        }

        await onSaveConnectionMapping(params.connectionId, {
            external_business_id: params.administrationId,
            external_business_name: params.administrationName ?? null,
            journal_ids: params.journals,
            schedule_enabled: false,
        });

        const response = await api.Partner__initiateBksDocumentImport({
            business_id: businessId,
            business_id_in_bks: params.administrationId,
            connection_id: params.connectionId,
            journal_ids: params.journals,
            date_from: params.startDate,
            date_to: params.endDate,
        });

        await onRefreshImportJobs();

        return {
            import_id: response.id,
            status: response.status,
        };
    }, [api, businessId, onRefreshImportJobs, onSaveConnectionMapping]);

    const onPollBKSImportStatus = useCallback(async (importId: string) => {
        return await api.Partner__getBksImportStatus(importId) as BKSImportStatusResponse;
    }, [api]);

    return {
        hasIntegration,
        connections,
        importJobs,
        mappings,
        isLoadingJobs,
        onLoadBKSAdministrations,
        onLoadBKSJournals,
        onInitiateBKSImport,
        onPollBKSImportStatus,
        onRefreshImportJobs,
        onSaveConnectionMapping,
        getConnectionMapping,
    };
}
